import { forwardRef, useCallback, useEffect, useMemo } from "react";
import { mdiClose, mdiChevronDown } from "@mdi/js";
import ReactSelect, { createFilter } from "react-select";
import { BaseElementProps, PanelColor, ThemeColor } from "../../props";
import {
  Button,
  ButtonProps,
  Icon,
  IconProps,
  InternalItemProps,
  Typography,
  TypographyContent,
} from "../../atoms";
import { addLocalContext, useTranslate } from "../../util/localization";
import { useTheme } from "../../util/ThemeProvider";
import { Menu } from "../Menu";
import {
  cn,
  getBackgroundStyles,
  getBorderStyles,
  getDisabledStyles,
  getFocusRingStyles,
  getForegroundStyles,
} from "../../styles";
import { Chip, ChipProps } from "../Chip";

const tintStyles: Partial<Record<ThemeColor, string>> = {
  [ThemeColor.primary]: "text-primary-600 dark:text-primary-400",
  [ThemeColor.secondary]: "text-secondary-600 dark:text-secondary-400",
  [ThemeColor.error]: "text-error-600 dark:text-error-400",
  [ThemeColor.warning]: "text-warning-600 dark:text-warning-400",
  [ThemeColor.success]: "text-success-600 dark:text-success-400",
};

/**
 * Configuration for a single select option.
 */
export interface SelectOption extends InternalItemProps {
  /**
   * The value associated with this option.
   * Can be any type that represents the option's data.
   */
  value: any;
  /**
   * Label content to display for the option.
   * Supports localized content or plain strings.
   */
  label: TypographyContent;
  /**
   * Whether this option is disabled and cannot be selected.
   * @default false
   */
  disabled?: boolean;
  /**
   * Additional props to customize the icon display in the option.
   */
  iconProps?: IconProps;
  /**
   * Additional props to customize the chip display in multi-select mode.
   */
  chipProps?: ChipProps;
  /**
   * Additional props to customize the button behavior in the dropdown.
   */
  buttonProps?: ButtonProps;
}

/**
 * Selected value type - can be a single option or array of options for multi-select.
 */
export type SelectValue = SelectOption | SelectOption[];

export interface SelectProps extends BaseElementProps {
  /**
   * Callback function called when the selected value changes.
   */
  onChange: (value: SelectValue) => void;
  /**
   * Array of available options to choose from.
   */
  options: SelectOption[];
  /**
   * Currently selected value(s).
   * Single option for regular select, array of options for multi-select.
   */
  value?: SelectValue;
  /**
   * Placeholder text shown when no option is selected.
   * @default { local: "Placeholder" }
   */
  placeholder?: TypographyContent;
  /**
   * Message to display when no options are available.
   * @default { local: "NoOptions" }
   */
  noOptions?: TypographyContent;
  /**
   * Message to display when search yields no results.
   * Includes the search term in the message context.
   * @default { local: "NoOptionsFor" }
   */
  noOptionsFor?: TypographyContent;
  /**
   * Enables selection of multiple options.
   * Changes display to show chips for selected values.
   * @default false
   */
  multi?: boolean;
  /**
   * Renders the value as only the selected option for a compact icon select.
   * @default false
   */
  iconOnly?: boolean;
  /**
   * Shows a clear button to reset the selection.
   * @default true
   */
  clearable?: boolean;
  /**
   * Background color theme for the select input.
   * @default PanelColor.level0
   */
  color?: PanelColor;
  /**
   * Applies error styling with red border and focus ring.
   * @default false
   */
  error?: boolean;
  /**
   * Reduces spacing and font sizes for a more compact appearance.
   * @default false
   */
  dense?: boolean;
  /**
   * Applies bold font weight to the selected value text.
   * @default false
   */
  strong?: boolean;
  /**
   * Removes border, background, and focus ring for embedding inside a parent container.
   * @default false
   */
  frameless?: boolean;
  /**
   * Applies a theme color tint to the selected value text and icon.
   */
  tint?: ThemeColor;
  /**
   * Hides the dropdown chevron indicator.
   * @default false
   */
  hideIndicator?: boolean;
  /**
   * Makes the select non-interactive without the disabled visual appearance.
   * @default false
   */
  readOnly?: boolean;
}

const defaultFilter = createFilter();

/**
 * Advanced select component built on react-select with support for icons, multi-select,
 * and localized content. Provides searchable dropdown with customizable styling, error states,
 * and accessibility features. Supports both single and multi-select modes with consistent
 * theming and automatic positioning.
 */
export const Select = forwardRef<any, SelectProps>(
  (
    {
      className,
      disabled,
      onChange,
      options,
      value,
      placeholder = { local: "Placeholder" },
      noOptions = { local: "NoOptions" },
      noOptionsFor = { local: "NoOptionsFor" },
      multi = false,
      clearable = true,
      color = PanelColor.level0,
      error = false,
      dense = false,
      strong = false,
      iconOnly = false,
      frameless = false,
      tint,
      hideIndicator = false,
      readOnly = false,
    },
    ref,
  ) => {
    const [t] = useTranslate();
    const { isGlobalDarkMode } = useTheme();
    const processedOptions = useMemo(() => {
      return options.map((option) => ({
        ...option,
        isDisabled: option.disabled,
      }));
    }, [options]);

    const selectStyles = {
      container: (base) => ({
        ...base,
        pointerEvents: "auto",
        cursor: disabled ? "not-allowed" : "auto",
      }),
      valueContainer: (base) => ({
        ...base,
        padding: dense ? "0 4px" : "2px 8px",
      }),
      input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
      }),
      menu: (provided) => ({
        ...provided,
        width: "max-content",
        minWidth: "100%",
        backgroundColor: "transparent",
        boxShadow: "none",
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      indicatorSeparator: (base) => ({ ...base, width: 0 }),
    };

    const filterOption = useCallback((option, input) => {
      if (!input) return true;
      if (!option.label.local) return defaultFilter(option, input);
      const label =
        typeof option.label.local === "object"
          ? t(
              option.label.local.key,
              option.label.local.context,
              option.label.local.opts,
            )
          : t(option.label.local);

      return defaultFilter({ ...option, label }, input);
    }, []);

    return (
      <ReactSelect
        ref={ref}
        classNames={{
          valueContainer: () => "gap-1",
          menuPortal: () => (isGlobalDarkMode ? "dark" : ""),
        }}
        placeholder={
          <Typography.Span
            className={cn(getForegroundStyles(color), "opacity-50")}
            content={placeholder}
          />
        }
        isMulti={multi}
        isClearable={clearable}
        menuPlacement="auto"
        styles={selectStyles}
        isDisabled={disabled || readOnly}
        menuPortalTarget={document.body}
        noOptionsMessage={({ inputValue }) =>
          inputValue && inputValue.length > 0 ? (
            <Typography.Span
              className="text-xs"
              muted
              content={addLocalContext(noOptionsFor, { value: inputValue })}
            />
          ) : (
            <Typography.Span className="text-xs" muted content={noOptions} />
          )
        }
        components={{
          Control,
          MenuList,
          Option,
          SingleValue,
          MultiValue,
          ClearIndicator,
          DropdownIndicator: hideIndicator ? () => null : DropdownIndicator,
        }}
        options={processedOptions}
        value={value}
        onChange={onChange}
        filterOption={filterOption}
        {...({
          dense,
          strong,
          color,
          error,
          iconOnly,
          frameless,
          tint,
          hideIndicator,
          readOnly,
          className,
        } as any)}
      />
    );
  },
);

Select.displayName = "Select";

function Control({ children, innerProps, innerRef, isDisabled, selectProps }) {
  const { dense, strong, error, color, frameless, readOnly, className } = selectProps;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={cn(
        "flex pr-0",
        dense ? "py-0 text-xs min-h-[30px]" : "py-0.5 text-sm min-h-[38px]",
        frameless
          ? "bg-transparent border-none"
          : cn(
              "rounded-md border",
              dense ? "rounded" : "rounded-md",
              "focus-within:outline-none focus-within:ring-2",
              error
                ? getFocusRingStyles(ThemeColor.error)
                : getFocusRingStyles(ThemeColor.primary),
              getBackgroundStyles(color),
              getBorderStyles(error ? ThemeColor.error : ThemeColor.gray),
            ),
        getForegroundStyles(color),

        strong && "font-medium",
        isDisabled && !readOnly && getDisabledStyles(),
        innerProps.className,
        className,
      )}
    >
      {children}
    </div>
  );
}

function SingleValue({ data, innerProps, selectProps }) {
  const tintClass = selectProps.tint ? tintStyles[selectProps.tint] : undefined;
  return (
    <div
      className={cn(
        "col-start-1 col-end-3 row-start-1 row-end-2 max-w-full ml-0.5 mr-0.5 box-border flex items-center gap-1 overflow-hidden",
        tintClass,
      )}
    >
      <Icon
        color={tintClass ? selectProps.tint : ThemeColor.none}
        muted={!tintClass}
        size={15}
        path={data.iconPath}
        name={data.iconName}
        local={data.iconLocal}
        {...data.iconProps}
      />
      {!selectProps.iconOnly && (
        <Typography.Span
          noWrap
          content={data.label}
          showTitle
          {...innerProps}
        />
      )}
    </div>
  );
}

function MultiValue({ data, innerProps, removeProps }) {
  return (
    <Chip
      className="overflow-hidden"
      iconPath={data.iconPath}
      iconName={data.iconName}
      iconLocal={data.iconLocal}
      onAction={removeProps.onClick}
      buttonProps={removeProps}
      dense
      {...data.chipProps}
    >
      <Typography.Span noWrap content={data.label} showTitle {...innerProps} />
    </Chip>
  );
}

function MenuList({ innerRef, maxHeight, children }) {
  return (
    <Menu
      className="overflow-y-auto rounded-md border border-back-200 dark:border-back-700 bg-white dark:bg-back-800 shadow-lg"
      style={{ maxHeight }}
      ref={innerRef}
    >
      {children}
    </Menu>
  );
}

function Option({ data, innerRef, isFocused, innerProps }) {
  return (
    <Menu.Button
      className="text-left"
      color={isFocused ? ThemeColor.primary : ThemeColor.gray}
      iconPath={data.iconPath}
      iconName={data.iconName}
      iconLocal={data.iconLocal}
      ref={innerRef}
      disabled={data.isDisabled}
      {...data.buttonProps}
      {...innerProps}
    >
      <Typography.Span content={data.label} />
    </Menu.Button>
  );
}

function ClearIndicator({ isFocused, isDisabled, innerProps }) {
  return (
    <Button
      tabIndex={-1}
      className={cn(!isFocused && "opacity-60")}
      iconPath={mdiClose}
      {...innerProps}
      dense
      disabled={isDisabled}
      color={ThemeColor.none}
    />
  );
}

function DropdownIndicator({ isFocused, isDisabled, innerProps, selectProps }) {
  return (
    <Button
      tabIndex={-1}
      className={cn(
        "transition-transform",
        selectProps.menuIsOpen && "rotate-180",
        !isFocused && "opacity-60",
      )}
      iconPath={mdiChevronDown}
      {...innerProps}
      dense
      disabled={isDisabled}
      color={ThemeColor.none}
    />
  );
}
